import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Title, Text, Card, Button } from '@tremor/react'
import { CogIcon, PlusIcon, TrashIcon, PencilIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { getResearchMethodologies, createResearchMethodology, updateResearchMethodology, deleteResearchMethodology, generateMethodologyAISuggestions } from '../../Requests/ResearchMethodologyRequests'
import { sanitizeUserInput } from '../../utils/security'
import { useMyContext } from '../ContextProviders/CreditsContextProvider'
import UpgradedLoader from '../UpgradedLoader'
import ConfirmationModal from '../ConfirmationModal'
import MethodologyFormModal from './MethodologyFormModal'
import MethodologyAIGenerationModal from './MethodologyAIGenerationModal'

function MetodologiaTab({ projectUuid, onNavigateNext }) {
    const { t } = useTranslation()
    const { setCredits } = useMyContext()
    
    // Methodologies state
    const [methodologies, setMethodologies] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // UI state
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [methodologyToDelete, setMethodologyToDelete] = useState(null)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [methodologyToEdit, setMethodologyToEdit] = useState(null)
    const [modalLoading, setModalLoading] = useState(false)
    const [showAIModal, setShowAIModal] = useState(false)
    const [aiLoading, setAILoading] = useState(false)

    const loadMethodologies = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await getResearchMethodologies(projectUuid)
            setMethodologies(data.data || [])
        } catch (err) {
            console.error('Failed to load research methodologies:', err)
            setError(t('Research.Methodology.LoadError'))
        } finally {
            setLoading(false)
        }
    }, [projectUuid, t])

    useEffect(() => {
        loadMethodologies()
    }, [loadMethodologies])

    const handleDeleteClick = (methodologyUuid) => {
        setMethodologyToDelete(methodologyUuid)
        setShowDeleteModal(true)
    }

    const handleDeleteConfirm = async () => {
        if (!methodologyToDelete) return

        try {
            await deleteResearchMethodology(projectUuid, methodologyToDelete)
            setMethodologies(prev => prev.filter(m => m.uuid !== methodologyToDelete))
            setShowDeleteModal(false)
            setMethodologyToDelete(null)
        } catch (err) {
            console.error('Failed to delete research methodology:', err)
            alert(err.error || t('Research.Methodology.DeleteError'))
        }
    }

    const handleCreateManual = () => {
        setShowCreateModal(true)
    }

    const handleGenerateAI = () => {
        setShowAIModal(true)
    }

    const handleAIGenerationSubmit = async (responses) => {
        setAILoading(true)
        try {
            const response = await generateMethodologyAISuggestions(projectUuid, responses)
            
            if (response.data) {
                // Add generated methodologies to the list
                if (Array.isArray(response.data)) {
                    setMethodologies(prev => [...response.data, ...prev])
                } else {
                    setMethodologies(prev => [...prev, response.data])
                }
                
                // Update credits if available
                if (response.total_credits_used) {
                    console.log(`Used ${response.total_credits_used} credits for AI methodology generation`)
                }
                
                setShowAIModal(false)
            }
        } catch (err) {
            console.error('Failed to generate AI methodology suggestions:', err)
            throw err
        } finally {
            setAILoading(false)
        }
    }

    const handleEditMethodology = (methodologyUuid) => {
        const methodology = methodologies.find(m => m.uuid === methodologyUuid)
        if (methodology) {
            setMethodologyToEdit(methodology)
            setShowEditModal(true)
        }
    }

    const handleCreateSubmit = async (methodologyData) => {
        setModalLoading(true)
        try {
            const response = await createResearchMethodology(projectUuid, methodologyData)
            if (response.data) {
                setMethodologies(prev => [...prev, response.data])
                setShowCreateModal(false)
                
                // Navigate to next step after creating methodology
                if (onNavigateNext) {
                    setTimeout(() => onNavigateNext(), 500)
                }
            }
        } catch (err) {
            console.error('Failed to create research methodology:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const handleEditSubmit = async (methodologyData) => {
        if (!methodologyToEdit) return
        
        setModalLoading(true)
        try {
            const response = await updateResearchMethodology(projectUuid, methodologyToEdit.uuid, methodologyData)
            if (response.data) {
                setMethodologies(prev => prev.map(m => 
                    m.uuid === methodologyToEdit.uuid ? response.data : m
                ))
                setShowEditModal(false)
                setMethodologyToEdit(null)
            }
        } catch (err) {
            console.error('Failed to update research methodology:', err)
            throw err
        } finally {
            setModalLoading(false)
        }
    }

    const handleSelectMethodology = async (methodologyUuid) => {
        try {
            // First deactivate all other methodologies
            const updatePromises = methodologies.map(m => {
                if (m.uuid === methodologyUuid) {
                    return updateResearchMethodology(projectUuid, m.uuid, {
                        methodology_type: m.methodology_type,
                        research_design: m.research_design,
                        data_collection: m.data_collection,
                        analysis_approach: m.analysis_approach,
                        reasoning: m.reasoning,
                        status: 'active'
                    })
                } else if (m.status === 'active') {
                    return updateResearchMethodology(projectUuid, m.uuid, {
                        methodology_type: m.methodology_type,
                        research_design: m.research_design,
                        data_collection: m.data_collection,
                        analysis_approach: m.analysis_approach,
                        reasoning: m.reasoning,
                        status: 'draft'
                    })
                }
                return Promise.resolve()
            })

            await Promise.all(updatePromises)
            
            // Update local state immediately
            setMethodologies(prev => 
                prev.map(m => ({
                    ...m,
                    status: m.uuid === methodologyUuid ? 'active' : (m.status === 'active' ? 'draft' : m.status)
                }))
            )
            
            // Navigate to next step after selecting a methodology
            if (onNavigateNext) {
                setTimeout(() => onNavigateNext(), 500)
            }
        } catch (err) {
            console.error('Failed to select research methodology:', err)
            alert(err.error || t('Research.Methodology.SelectError'))
        }
    }

    const getMethodologyTypeBadge = (type) => {
        const typeColors = {
            qualitative: 'bg-green-100 text-green-800',
            quantitative: 'bg-blue-100 text-blue-800',
            mixed: 'bg-purple-100 text-purple-800'
        }
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeColors[type] || typeColors.qualitative}`}>
                {t(`Research.Methodology.Types.${type}`)}
            </span>
        )
    }

    const getStatusBadge = (status) => {
        const statusColors = {
            draft: 'bg-gray-100 text-gray-800',
            active: 'bg-iavisus-900 text-white',
            archived: 'bg-yellow-100 text-yellow-800'
        }
        
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.draft}`}>
                {t(`Research.Methodology.Status.${status}`)}
            </span>
        )
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-iavisus-100" />
            </div>
        )
    }

    return (
        <div data-testid="metodologia-container" className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <CogIcon className="h-6 w-6 text-iavisus-100" />
                        <Title className="text-lg font-semibold">
                            {t('Research.Methodology.Title')}
                        </Title>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            data-testid="metodologia-create-btn"
                            size="sm"
                            icon={PlusIcon}
                            onClick={handleCreateManual}
                            className="bg-white border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-50"
                        >
                            {t('Research.Methodology.CreateManual')}
                        </Button>
                        <Button
                            data-testid="metodologia-ai-btn"
                            size="sm"
                            icon={SparklesIcon}
                            onClick={handleGenerateAI}
                            className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                        >
                            {t('Research.Methodology.GenerateAI')}
                        </Button>
                    </div>
                </div>
                
                <Text className="text-gray-600 mb-6">
                    {t('Research.Methodology.Description')}
                </Text>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                )}

                {methodologies.length === 0 ? (
                    <div data-testid="metodologia-empty" className="bg-gray-50 rounded-lg p-8 text-center">
                        <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <Text className="text-gray-500 mb-2">
                            {t('Research.Methodology.EmptyState')}
                        </Text>
                        <Text className="text-gray-400 text-sm">
                            {t('Research.Methodology.EmptyStateHint')}
                        </Text>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {methodologies.map((methodology) => (
                            <Card key={methodology.uuid} data-testid={`metodologia-item-${methodology.uuid}`} className="p-4">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <span className={`inline-block w-2 h-2 rounded-full ${
                                                methodology.type === 'ai_generated' ? 'bg-iavisus-100' : 'bg-blue-500'
                                            }`}></span>
                                            <Text className="text-xs text-gray-500">
                                                {methodology.type === 'ai_generated' ? 
                                                    t('Research.Methodology.AIGenerated') : 
                                                    t('Research.Methodology.ManualType')
                                                }
                                            </Text>
                                            {getMethodologyTypeBadge(methodology.methodology_type)}
                                            {getStatusBadge(methodology.status)}
                                        </div>
                                        
                                        <div className="mb-3">
                                            <Text className="font-medium text-gray-900 mb-1">
                                                {t('Research.Methodology.MethodologyType')}:
                                            </Text>
                                            <Text className="text-gray-700">
                                                {methodology.methodology_type || t('Research.Methodology.EmptyMethodology')}
                                            </Text>
                                        </div>

                                        {methodology.research_design && (
                                            <div className="mb-3">
                                                <Text className="font-medium text-gray-900 mb-1">
                                                    {t('Research.Methodology.ResearchDesign')}:
                                                </Text>
                                                <Text className="text-gray-700 text-sm">
                                                    {methodology.research_design}
                                                </Text>
                                            </div>
                                        )}

                                        {methodology.data_collection && (
                                            <div className="mb-3">
                                                <Text className="font-medium text-gray-900 mb-1">
                                                    {t('Research.Methodology.DataCollection')}:
                                                </Text>
                                                <Text className="text-gray-700 text-sm">
                                                    {methodology.data_collection}
                                                </Text>
                                            </div>
                                        )}

                                        {methodology.analysis_approach && (
                                            <div className="mb-3">
                                                <Text className="font-medium text-gray-900 mb-1">
                                                    {t('Research.Methodology.AnalysisApproach')}:
                                                </Text>
                                                <Text className="text-gray-700 text-sm">
                                                    {methodology.analysis_approach}
                                                </Text>
                                            </div>
                                        )}

                                        {methodology.reasoning && (
                                            <details className="mb-2">
                                                <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800">
                                                    {t('Research.Methodology.ShowReasoning')}
                                                </summary>
                                                <Text className="text-sm text-gray-600 mt-2 pl-4 border-l-2 border-gray-200">
                                                    {methodology.reasoning}
                                                </Text>
                                            </details>
                                        )}
                                        
                                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                                            <span>{t('Research.Methodology.CreatedBy')} {methodology.user?.name || t('Common.Unknown')}</span>
                                            <span>{new Date(methodology.created_at).toLocaleDateString()}</span>
                                            {methodology.credits_used > 0 && (
                                                <span>{methodology.credits_used} {t('Common.Credits')}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        {methodology.status !== 'active' && (
                                            <button
                                                type="button"
                                                onClick={() => handleSelectMethodology(methodology.uuid)}
                                                className="px-3 py-1 text-sm bg-iavisus-100 text-white rounded-md hover:bg-iavisus-200 transition-colors"
                                                title={t('Research.Methodology.ClickToSelect')}
                                            >
                                                {t('Research.Methodology.Select')}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleEditMethodology(methodology.uuid)}
                                            className="p-1 text-gray-500 hover:text-gray-700"
                                            title={t('Common.Edit')}
                                        >
                                            <PencilIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(methodology.uuid)}
                                            className="p-1 text-red-500 hover:text-red-700"
                                            title={t('Common.Delete')}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Methodology Modal */}
            <MethodologyFormModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={handleCreateSubmit}
                loading={modalLoading}
            />

            {/* Edit Methodology Modal */}
            <MethodologyFormModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false)
                    setMethodologyToEdit(null)
                }}
                onSubmit={handleEditSubmit}
                editMethodology={methodologyToEdit}
                loading={modalLoading}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                title={t('Common.Delete')}
                message={t('Research.Methodology.DeleteConfirm')}
                confirmText={t('Common.Delete')}
                cancelText={t('Common.Cancel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
            />

            {/* AI Methodology Generation Modal */}
            <MethodologyAIGenerationModal
                isOpen={showAIModal}
                onClose={() => setShowAIModal(false)}
                onSubmit={handleAIGenerationSubmit}
                loading={aiLoading}
            />
        </div>
    )
}

export default MetodologiaTab